export function normalizeBackendUrl(raw?: string): string {
  let value = (raw ?? "").trim().replace(/\/$/, "");
  if (!value) return "";
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value}`;
  }
  // If env is ".../api", strip it so callers never build ".../api/api/..." (common Vercel/Railway mistake).
  value = value.replace(/\/api\/?$/i, "");
  return value;
}

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);

export async function fetchWithAuth<T = any>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem("token");
  const headers = new Headers(options?.headers ?? {});

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (
    options?.body !== undefined &&
    !headers.has("Content-Type") &&
    typeof options.body === "string"
  ) {
    headers.set("Content-Type", "application/json");
  }

  const url = `${BACKEND_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers,
    });
  } catch (err) {
    throw new Error(
      `Network request failed (${url}). Check connectivity and CSP; unset VITE_BACKEND_URL uses same-origin /api.`,
    );
  }

  if (res.status === 401) {
    localStorage.removeItem("token");
    window.location.href = "/";
    throw new Error("Unauthorized");
  }

  let data: unknown = null;
  if (res.status !== 204) {
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      data = await res.json().catch(() => ({}));
    } else if (contentType.startsWith("text/")) {
      data = await res.text().catch(() => "");
    } else {
      data = await res.blob().catch(() => null);
    }
  }

  if (!res.ok) {
    const errObj = data as { error?: string; message?: string } | null;
    const message = errObj?.error ?? errObj?.message ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  return data as T;
}
